// Switch_Core_React_Standards.js

/**
 * 🎯 THE  SOURCE NORMALIZER
 * Purpose:
 *   - Accept "non-React-compliant" ASP.NET Core responses (payload key varies: batch, user, result, etc.)
 *   - Emit a stable React-friendly shape: { success, message, data }
 *   - Prepare outbound React payloads for ASP.NET Core by unwrapping { data: ... } when appropriate
 *   - Do this WITHOUT knowing domain keys/values (no manual “add a new thing” updates)
 */
export const GoldenSourceHelper = (() => {
  // Keys that are part of the response envelope, not the domain payload.
  // Add only truly cross-cutting envelope keys here (rare).
  const RESERVED_KEYS = new Set([
    "success",
    "Success",
    "message",
    "Message",
    "status",
    "Status",
    "errors",
    "Errors",
    "error",
    "Error",
    "warnings",
    "Warnings",
  ]);

  /**
   * Casing-tolerant property fetch:
   * - looks for PascalCase first, then camelCase (your current convention)
   */
  const get = (obj, key) => {
    if (!obj || typeof obj !== "object") return null;

    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
    const camel = key.charAt(0).toLowerCase() + key.slice(1);

    if (obj[pascal] !== undefined) return obj[pascal];
    if (obj[camel] !== undefined) return obj[camel];

    return null;
  };

  /**
   * Auto-discovers the "primary payload" from a raw response object by excluding
   * reserved envelope keys.
   *
   * Expected shapes:
   *   { success, message, data: {...} }            -> returns raw.data
   *   { success, message, batch: {...} }           -> returns raw.batch
   *   { Success, Message, Result: {...} }          -> returns raw.Result
   *
   * Ambiguity handling:
   *   If multiple non-reserved keys exist (e.g. batch + metrics),
   *   we do NOT guess. We return null and include the keys for debugging.
   */
  const discoverPayload = (raw) => {
    if (!raw || typeof raw !== "object") {
      return { data: null, dataKey: null, candidates: [] };
    }

    // If a standard key exists, prefer it (most explicit contract)
    if (raw.data !== undefined) return { data: raw.data, dataKey: "data", candidates: ["data"] };
    if (raw.Data !== undefined) return { data: raw.Data, dataKey: "Data", candidates: ["Data"] };

    const candidates = Object.keys(raw).filter((k) => !RESERVED_KEYS.has(k));

    if (candidates.length === 1) {
      const k = candidates[0];
      return { data: raw[k], dataKey: k, candidates };
    }

    // 0 candidates = envelope only
    // >1 candidates = ambiguous payload (backend should wrap under data)
    return { data: null, dataKey: null, candidates };
  };

  /**
   * Normalizes ANY "reasonable" ASP.NET Core response into a stable shape:
   *   { success, message, data, dataKey, candidates, getProp }
   */
  const normalize = (raw) => {
    if (!raw) return null;

    const success = raw.success ?? raw.Success ?? false;
    const message = raw.message ?? raw.Message ?? "";

    const { data, dataKey, candidates } = discoverPayload(raw);

    // If ambiguous, warn loudly (this is a contract issue, not a UI issue)
    if (dataKey === null && candidates.length > 1) {
      // eslint-disable-next-line no-console
      console.warn("GoldenSourceHelper.normalize: Ambiguous payload keys detected.", {
        candidates,
        raw,
      });
    }

    return {
      success,
      message,
      data,
      dataKey,      // e.g. "batch"
      candidates,   // useful when ambiguous
      getProp: (propName) => get(data, propName),
    };
  };

  /**
   * Prepares outbound payloads for ASP.NET Core:
   * - If React sends { data: X } and ONLY that key, unwrap to X
   * - Otherwise, pass through unchanged
   */
  const prepareForApi = (payload) => {
    if (!payload || typeof payload !== "object") return payload;

    // Unwrap the common React envelope: { data: {...} }
    if (Object.keys(payload).length === 1 && payload.data !== undefined) {
      return payload.data;
    }

    return payload;
  };

  return {
    get,
    normalize,
    prepareForApi,

    // Expose for debugging/testing if you want
    _discoverPayload: discoverPayload,
    _RESERVED_KEYS: RESERVED_KEYS,
  };
})();
